/**
 * Sign in and sign up.
 *
 * The desk role is not set here: it comes from the user's firm (created or
 * joined after sign-up) and reaches the app as the token's `role` claim —
 * `refreshToken` mints a token that carries it once the firm is in place.
 */
import { gql } from '@apollo/client';

export const LOGIN_USER = gql`
  mutation Login($email: String!, $password: String!) {
    loginUser(email: $email, password: $password) {
      success
      message
      authToken
      refreshToken
      tokenType
      userId
      emailVerified
      mfaRequired
    }
  }
`;

export const SIGNUP_USER = gql`
  mutation Signup($user: UserCreate!) {
    signupUser(user: $user) {
      success
      message
      authToken
      refreshToken
      tokenType
      userId
      emailVerified
    }
  }
`;

/** A fresh access token — carries the firm `role` claim once the membership is approved. */
export const REFRESH_TOKEN = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      success
      authToken
      refreshToken
      userId
      emailVerified
    }
  }
`;

/**
 * Server-side end of a session.
 *
 * No arguments: the resolver reads the bearer token off the request and
 * revokes that session, which is why it must be called while the token is
 * still in localStorage — clear first and the call arrives anonymous and is
 * refused with a 401. Callers treat it as best-effort: the local sign-out
 * proceeds whatever this answers, since a dead backend must not trap someone
 * in a session.
 */
export const LOGOUT_USER = gql`
  mutation Logout {
    logoutUser {
      success
      message
    }
  }
`;

/* ── setting a password without one ──────────────────────────────────────
 *
 * Three calls, two paths, one ending. Both finish at `resetPassword`, which
 * takes a TOKEN — never the old password, because the whole point is that the
 * person does not have one.
 *
 *   email → the server mails a reset LINK to /reset-password?token=…, and the
 *           token arrives in the URL. There is no OTP to type.
 *   sms   → the server texts an OTP; `verifyForgotPasswordOtp` exchanges that
 *           OTP for the reset token, and the reset happens on the same screen.
 *
 * This is also how a NEW EMPLOYEE gets their first password. The backend
 * creates their user row with a deliberately unusable password hash and
 * expects them to set a real one here — so an employee added in the console
 * cannot sign in until this flow has been run for their address.
 */

/** Start it. `method` is "email" (reset link) or "sms" (OTP). */
export const SEND_FORGOT_PASSWORD_OTP = gql`
  mutation SendForgotPasswordOtp($input: ForgotPasswordInput!) {
    sendForgotPasswordOtp(input: $input) { success message }
  }
`;

/** The SMS path only: trade the OTP for the reset token. */
export const VERIFY_FORGOT_PASSWORD_OTP = gql`
  mutation VerifyForgotPasswordOtp($input: VerifyOTPInput!) {
    verifyForgotPasswordOtp(input: $input) { success message resetToken }
  }
`;

/** The end of both paths: the token, and the password to set. */
export const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) { success message }
  }
`;

/**
 * Changing a password you already know, from inside a session.
 *
 * It answers with the USER, not a {success, message} envelope — so a returned
 * id is the confirmation, and a failure arrives as a GraphQL error rather than
 * a success flag set false.
 */
export const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($currentPassword: String!, $newPassword: String!) {
    updatePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      id
      email
    }
  }
`;

/**
 * BoloDhan's own sign-in: a phone number, then the OTP it was sent. The first
 * call returns a server-minted session id that the second must echo back —
 * the OTP alone is never enough (app/services/phone_otp_store.py on the
 * backend). The answer to the second call is the same AuthResponse the
 * email login gives, so the session lands the same way.
 */
export const LOGIN_WITH_PHONE = gql`
  mutation LoginWithPhone($input: PhoneLoginInput!) {
    loginWithPhone(input: $input) {
      success
      message
      isNewUser
      requiresName
      sessionId
    }
  }
`;

export const VERIFY_PHONE_LOGIN_OTP = gql`
  mutation VerifyPhoneLoginOtp($input: VerifyPhoneOtpInput!) {
    verifyPhoneLoginOtp(input: $input) {
      success
      message
      authToken
      refreshToken
      tokenType
      userId
      emailVerified
      mfaRequired
    }
  }
`;
